import CollapsibleList from 'components/CollapsibleList'
import OrdersGroup from 'components/CollapsibleList/OrdersGroup'
import EmptyMessage from 'components/EmptyMessage'
import { FixedWarning } from 'components/FixedWarnings/FixedWarning'
import { FooterMsg } from 'components/FooterMsg'
import { differenceInCalendarMonths, differenceInCalendarWeeks } from 'date-fns'
import { map } from 'lodash-es'
import * as PropTypes from 'prop-types'
import { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import API from 'state/API'
import Order from 'state/models/Order'
import { useOrders } from 'state/Orders'
import { useSettings } from 'state/Settings'
import {
	Donkey, isFuture, isThisMonth, isThisWeek, isToday, isTomorrow, isYesterday, newDate, useForcedRefresher,
} from 'utils'

OrdersList.countFuture = orders => orders.filter(o => o.is_considered_future).length
OrdersList.MAX_LENGTH = 500

/**
 * @param isFutureList {boolean} If it should create detailed groups named after future or past periods
 * @return {function(Order[]):object}
 */
function ordersSorter(isFutureList) {
	const today = newDate(null)

	//region a bunch of translation comments
	// t('today', { context: 'orders for...' })
	// t('yesterday')
	// t('tomorrow')
	// t('this week')
	// t('next week')
	// t('this month')
	// t('next month')
	// t('earlier', { context: 'orders for...' })
	// t('later', { context: 'orders for...' })
	// t('future', { context: 'orders for...' })
	//endregion
	return orders => {
		//TODO this is probably running twice - this was noticed when this `groups` variable was defined outside of this closure, and then it caused all orders to get duplicated in some scenarios. One which was reproducible was going back from another screen, into the Orders List.
		const groups = isFutureList?
			{ today: [], tomorrow: [], thisWeek: [], nextWeek: [], thisMonth: [], nextMonth: [], later: [] } :
			{ today: [], yesterday: [], earlier: [], future: [] } //IIRC, yesterday/future do not happen in practice nowadays?
		const keys = Object.keys(groups)

		orders.forEach(order => {
			let selectedArray = isFutureList? groups.later : groups.earlier
			if (isToday(order.ready_at)) {
				selectedArray = groups[keys[0]]
			} else if (
				(isFutureList && isTomorrow(order.ready_at)) ||
				(!isFutureList && isYesterday(order.ready_at))
			) {
				selectedArray = groups[keys[1]]
			} else if (!isFutureList && isFuture(order.ready_at)) {
				selectedArray = groups[keys[3]]
				//from here on, the current/search screens won't get more divisions - see https://is.gd/nV6n8I
			} else if (isFutureList) {
				if (isThisWeek(order.ready_at)) {
					selectedArray = groups[keys[2]]
				} else if (Math.abs(differenceInCalendarWeeks(order.ready_at, today)) == 1) {
					selectedArray = groups[keys[3]]
				} else if (isThisMonth(order.ready_at)) {
					selectedArray = groups[keys[4]]
				} else if (Math.abs(differenceInCalendarMonths(order.ready_at, today)) == 1) {
					selectedArray = groups[keys[5]]
				}
			}
			selectedArray.push(order)
		})
		return groups
	}
}

//FIXME rendering three times, not sure why, but seems related to the Routes itself? :( might get fixed in the router upgrades done in some other forgotten branch
export default function OrdersList({ isFuture, allOpen = false, status = undefined }) {
	const { t }    = useTranslation()
	const { sort } = useSettings()
	const sortCreated = (sort == 'created')

	const { status: qsStatus } = useParams()
	status ||= qsStatus

	let orders  = useOrders(status)
	let maxedOutMsg
	if (orders.length > OrdersList.MAX_LENGTH) {
		const direction = sortCreated? t('newest') : t('earliest due')
		const count     = orders.length - OrdersList.MAX_LENGTH
		orders          = orders.slice(0, OrdersList.MAX_LENGTH)
		maxedOutMsg     = <Trans count={count}><b>We only show the {{ max: OrdersList.MAX_LENGTH }} {{ direction }} orders.</b> To see the other {{count}}, adjust the selected date range.</Trans>
	}

	//rerenders the list once the number of future orders change, given the current time
	//NOTE: this could be further optimized if we start checking when is the nearest order due to leave the future list, but it seems oversimplification of a simple filter?
	const refresh     = useForcedRefresher()
	const totalFuture = OrdersList.countFuture(orders)
	useEffect(() => {
		return () => window.clearInterval(
			window.setInterval(() => {
				const newTotal = OrdersList.countFuture(orders)
				if (totalFuture != newTotal) {
					refresh() //the rerender will already cause a recount of totalFuture
				}
			}, Donkey.ORDERS_INTERVAL),
		)
	})

	if (!orders.length) {
		const emptyHelpText =
			      (status == Order.STATUS.ACTIVE)? t('As soon as you receive one, it will appear here 😃') :
			      (status == Order.STATUS.COMPLETED)? t('As soon as you finish one, it will appear here 😃') :
				      null
	  return <EmptyMessage icon={Order.ICONS[status]} resource={API.RESOURCES.ORDERS} msg={emptyHelpText}
	                       defaultMsg={t('Currently you have no {{status}} orders.', { status: t(status, { context: 'orders' }) })}
	  />
	}

	const sorter      = ordersSorter(isFuture)
	const groups      = sorter(orders)
	const noHighlight = status != Order.STATUS.ACTIVE
	let oneIsOpen     = 0
	return <>
		<FixedWarning variant="warning">{maxedOutMsg}</FixedWarning>
		<CollapsibleList headerLeft={t('Order')}
		                 headerRight={<>{sortCreated && <small>{t('(newest first)')}</small>} {t('Due at')}</>}>
			{map(groups, (orders, period) => (
				!!orders.length &&
				<OrdersGroup {...{ orders, noHighlight, period }} key={period} openByDefault={allOpen || !oneIsOpen++}/>
			))}
		</CollapsibleList>
		<FooterMsg>{maxedOutMsg}</FooterMsg>
	</>
}

OrdersList.propTypes = {
	isFuture: PropTypes.bool.isRequired,
	allOpen : PropTypes.bool,
}
